import { useState, useEffect } from 'react';
import { useI18n, formatDay } from '../../i18n';
import { useLabels } from '../../i18n/labels';
import { getWeightClasses, createBout, updateBout, assignBoutJury } from '../../api/client';
import { LockIcon, WarningIcon } from '../shared/Icons';
import ModalBackdrop from '../shared/ModalBackdrop';
import ModalHeader from '../shared/ModalHeader';
import ModalBody from '../shared/ModalBody';
import ModalFooter from '../shared/ModalFooter';
import { personName } from '../../utils/personName';
import { formatWarnings, inlineError, type ApiWarning } from '../../hooks/useApiCall';
import { useBusy } from '../../hooks/useBusy';
import { useToast } from '../../hooks/useToast';
import { useConfirm } from '../../hooks/useConfirm';
import type {
  Bout, BoutReferee, BoutStatus, BoutWriteBody, Ring, Registration, Stage, TournamentReferee,
  JuryPoolMember, AgeGroup, WeightClass, Gender,
} from '../../types';

// ── Bout time formula ─────────────────────────────────────────────────────────
// Based on IFMA 2025/2026 Technical Rules
import { DEFAULT_ROUNDS, ROUND_TIMING, boutRounds, deriveAgeGroup, deriveWeightClass, roundFormula } from '../../utils/tournament';
import { formatClock } from '../../hooks/useRoundClock';
import { runsAsOpenRing } from '../../utils/tournamentType';
import CornerPicker, { useCornerState, type CornerAthlete } from './CornerPicker';
import Notice from '../shared/Notice';
import Field from '../shared/Field';

export interface BoutFormModalProps {
  mode: 'create' | 'edit';
  bout?: Bout;          // present in edit mode
  tournamentId: string;
  tournamentType: string;
  rings: Ring[];
  referees: TournamentReferee[];      // all pool members
  juryPool: JuryPoolMember[];
  registrations: Registration[];
  bouts: Bout[];
  tournamentDays: string[];
  isMultiDay: boolean;
  ageGroups: AgeGroup[];
  /** Warnings is the non-blocking advisory list from the backend response
   *  (e.g. rest-gap violations). Caller is responsible for surfacing them. */
  onDone: (warnings?: ApiWarning[]) => void;
  onClose: () => void;
  onDelete?: (boutId: string) => void;
}

export default function BoutFormModal({
  mode, bout, tournamentId, tournamentType, rings, referees, juryPool,
  registrations, bouts: allBouts, tournamentDays, isMultiDay, ageGroups, onDone, onClose, onDelete,
}: BoutFormModalProps) {
  const isEdit = mode === 'edit';
  const { t } = useI18n();
  const { genderLabel } = useLabels();
  const { showError, showWarning } = useToast();
  const { confirm } = useConfirm();

  // ── Form state ─────────────────────────────────────────────────────────────
  const nextBoutNumber = isEdit ? String(bout!.bout_number) : String((allBouts.reduce((max, b) => Math.max(max, b.bout_number || 0), 0) + 1));
  const [boutNumber,   setBoutNumber]   = useState(nextBoutNumber);
  const [ringId,       setRingId]       = useState(isEdit ? (bout!.ring_id || '') : (rings.length === 1 ? rings[0].id : ''));
  const [stage,        setStage]        = useState<Stage | ''>(isEdit ? (bout!.stage || '') : (runsAsOpenRing(tournamentType) ? 'Challenge' : ''));
  const [boutDate,     setBoutDate]     = useState(isEdit && bout!.bout_date ? String(bout!.bout_date).slice(0,10) : (tournamentDays[0] || ''));
  const [status,       setStatus]       = useState<BoutStatus>(isEdit ? (bout!.status || 'pending') : 'pending');
  // #664 — the state of a corner is one hook (the picker is CornerPicker). The red one comes first.
  const red = useCornerState(bout, 'red');
  const blue = useCornerState(bout, 'blue');
  const { athleteId: redId, sourceBout: redSourceBout } = red;
  const { athleteId: blueId, sourceBout: blueSourceBout } = blue;
  const [ringRefId,    setRingRefId]    = useState(isEdit ? (bout!.ring_referee_id || '') : '');
  const [juryId,       setJuryId]       = useState('');
  const [ageGroup,     setAgeGroup]     = useState(isEdit ? (bout!.bout_age_group || '') : '');
  const [weightClass,  setWeightClass]  = useState(isEdit ? (bout!.bout_weight_class || '') : '');
  const [rounds,       setRounds]       = useState(isEdit ? boutRounds(bout!) : DEFAULT_ROUNDS);
  // #605 — the lock of Save: a second tap sends no second bout. The trash is also disabled while the save runs.
  const save = useBusy();
  const [err,          setErr]          = useState('');

  // Weight class options for selected age group
  const [wcOptions, setWcOptions] = useState<WeightClass[]>([]);

  // ── Athlete options ────────────────────────────────────────────────────────
  // Open Ring + create mode: scope the picker to confirmed athletes only.
  // The same boxer is intentionally allowed to appear in multiple bouts (Open
  // Ring boxers can fight more than once on the same day) — Match Boxers'
  // auto-pair flow has its own "skip already-paired" logic that is unrelated
  // to this manual picker. Edit mode and Championship show all registrations.
  const allAthletes: CornerAthlete[] = registrations
    .filter(r => {
      if (runsAsOpenRing(tournamentType) && !isEdit) {
        if (!r.is_confirmed) return false;
      }
      return true;
    })
    .map(r => ({
      // Single canonical pointer post users-athlete-split refactor.
      key: r.athlete_record_id,
      label: `${personName(r.first_name, r.last_name)} ${r.age_group} ${r.weight_class} ${r.team_name || ''} ${r.gender || ''}`,
      first_name:   r.first_name,
      last_name:    r.last_name,
      age_group:    r.age_group,
      weight_class: r.weight_class,
      gender:       r.gender || '',
      team_name:    r.team_name || '',
      experience_bouts: r.experience_bouts ?? 0,
      current_weight_kg: r.current_weight_kg,
      age:          r.age,
    }));

  const redReg  = allAthletes.find(a => a.key === redId);
  const blueReg = allAthletes.find(a => a.key === blueId);

  // Gender from athlete OR source bout
  const redBoutGender  = redSourceBout ? (allBouts.find(b => b.id === redSourceBout)?.red_gender || allBouts.find(b => b.id === redSourceBout)?.blue_gender || '') : '';
  const blueBoutGender = blueSourceBout ? (allBouts.find(b => b.id === blueSourceBout)?.red_gender || allBouts.find(b => b.id === blueSourceBout)?.blue_gender || '') : '';
  const redGender  = redReg?.gender  || redBoutGender;
  const blueGender = blueReg?.gender || blueBoutGender;

  // Effective gender for filtering (from whichever corner has one)
  const filterGender = redGender || blueGender;

  // Once one corner is selected, the other corner only shows same-gender athletes
  const redOptions = allAthletes.filter(a =>
    a.key !== blueId && (blueGender ? a.gender === blueGender : true)
  );
  const blueOptions = allAthletes.filter(a =>
    a.key !== redId && (redGender ? a.gender === redGender : true)
  );

  // Filter bout sources by gender too
  const redBoutOptions = allBouts.filter(b => {
    if (bout && b.id === bout.id) return false;
    if (!blueGender) return true;
    const g = b.red_gender || b.blue_gender || '';
    return !g || g === blueGender;
  });
  const blueBoutOptions = allBouts.filter(b => {
    if (bout && b.id === bout.id) return false;
    if (!redGender) return true;
    const g = b.red_gender || b.blue_gender || '';
    return !g || g === redGender;
  });

  // A new athlete or source bout derives the class again
  function resetClass() { setAgeGroup(''); setWeightClass(''); }

  // Clear the opposite corner if it becomes gender-incompatible
  function handleRedChange(newId: string) {
    const incoming = allAthletes.find(a => a.key === newId);
    if (blueReg?.gender && incoming?.gender && blueReg.gender !== incoming.gender) blue.setAthleteId('');
    red.setAthleteId(newId); resetClass();
  }
  function handleBlueChange(newId: string) {
    const incoming = allAthletes.find(a => a.key === newId);
    if (redReg?.gender && incoming?.gender && redReg.gender !== incoming.gender) red.setAthleteId('');
    blue.setAthleteId(newId); resetClass();
  }

  const genderMismatch = !!(redGender && blueGender && redGender !== blueGender);

  // ── Auto-derive age group + weight class from athletes OR source bouts ──
  const redBoutInfo  = allBouts.find(b => b.id === redSourceBout);
  const blueBoutInfo = allBouts.find(b => b.id === blueSourceBout);

  // Gather age groups and weight classes from all sources (athletes + bouts)
  const allAgeGroups = [
    redReg?.age_group, blueReg?.age_group,
    redBoutInfo?.bout_age_group || redBoutInfo?.red_age_group,
    blueBoutInfo?.bout_age_group || blueBoutInfo?.red_age_group,
  ].filter(Boolean) as string[];

  const allWeightClasses = [
    redReg?.weight_class, blueReg?.weight_class,
    redBoutInfo?.bout_weight_class || redBoutInfo?.red_weight_class,
    blueBoutInfo?.bout_weight_class || blueBoutInfo?.red_weight_class,
  ].filter(Boolean) as string[];

  useEffect(() => {
    if (ageGroup) return;
    const derived = allAgeGroups.length ? allAgeGroups.reduce((a, b) => deriveAgeGroup(a, b) || a) : '';
    if (derived) setAgeGroup(derived);
  }, [redId, blueId, redSourceBout, blueSourceBout]);

  useEffect(() => {
    if (weightClass) return;
    const derived = allWeightClasses.length ? allWeightClasses.reduce((a, b) => deriveWeightClass(a, b) || a) : '';
    if (derived) setWeightClass(derived);
  }, [redId, blueId, redSourceBout, blueSourceBout]);

  // ── Load weight class options when age group changes ──────────────────────
  // The latest run writes (#661, bug 6): the select fires for each arrow key,
  // so two loads overlap, and the answer of the first one listed the classes
  // of the earlier age group.
  useEffect(() => {
    if (!ageGroup) { setWcOptions([]); return; }
    // Infer gender: if both athletes same gender, use it; else both
    const genders = [...new Set([redReg?.gender, blueReg?.gender].filter(Boolean))] as Gender[];
    const gender: Gender | undefined = genders.length === 1 ? genders[0] : undefined;
    let live = true;
    getWeightClasses(gender ? { age_group: ageGroup, gender } : { age_group: ageGroup })
      .then(r => { if (live) setWcOptions(r.data); })
      .catch(err => { if (live) { setWcOptions([]); showError(err); } });
    return () => { live = false; };
  }, [ageGroup, redId, blueId]);

  // #602 — 5 rounds only for a class whose max_rounds is 5 (an LMF option).
  // Another class sends 3, so a change to such a class resets the rounds.
  // The rounds change while the bout is pending only (409 ROUNDS_LOCKED).
  // A list with no max_rounds (a cached copy from before #602, a failed
  // load) keeps the rounds of the bout: the save must not change them. So
  // does the class of the bout while it stays: a max_rounds that fell later
  // must not turn a save of the date into a change of the rounds, which a
  // live or scored bout refuses (#602 review).
  const classMax = ageGroups.find(ag => ag.code === ageGroup)?.max_rounds;
  const keepsFive = isEdit && boutRounds(bout!) === 5 && (classMax === undefined || ageGroup === (bout!.bout_age_group || ''));
  const canFiveRounds = (classMax !== undefined && classMax >= 5) || keepsFive;
  const sentRounds = canFiveRounds ? rounds : DEFAULT_ROUNDS;
  const roundsLocked = isEdit && bout!.status !== 'pending';
  const timeFormula = roundFormula(ageGroup, sentRounds);

  // The ring referee is not a side judge and not a jury of the bout (#454):
  // not the jury picked here, and not the current jury. An edit saves the
  // ring referee BEFORE it replaces the jury, so the server compares the ring
  // referee with the current jury (JURY_CANNOT_BE_RING_REF). The jury list
  // leaves out the ring referee picked here.
  const sideJudgeIds: string[] = isEdit ? (bout!.referees || []).map((r: BoutReferee) => r.id) : [];
  const juryIds = [juryId, isEdit ? bout!.jury_id : undefined].filter(Boolean);
  const ringRefOptions = referees.filter(r => !sideJudgeIds.includes(r.id) && !juryIds.includes(r.id));
  const juryOptions = juryPool.filter(r => r.id !== ringRefId);

  // ── Submit ────────────────────────────────────────────────────────────────
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    await save.run(async () => {
      setErr('');
      // #605 — the warnings of a PATCH that committed. A refused jury POST after it shows them too.
      let warnings: ApiWarning[] = [];
      try {
        if (redId && blueId && redId === blueId) { setErr(t.boutForm.sameAthlete); return; }
        // Single canonical pointer post users-athlete-split — redId/blueId is the AR id directly.
        const redArId  = red.mode === 'athlete' ? (redId  || null) : null;
        const blueArId = blue.mode === 'athlete' ? (blueId || null) : null;
        // The ring, the age group and the weight class are required selects, so the form cannot reach
        // this line with an empty one. The guard keeps '' out of the body, which a create requires non-null.
        if (!ringId || !ageGroup || !weightClass) return;
        // #664, F07 — the fields that a create and an edit send alike. A cleared field goes as null.
        const shared = {
          ring_id:          ringId,
          ring_referee_id:  ringRefId || null,
          bout_date:        boutDate || null,
          bout_age_group:   ageGroup,
          bout_weight_class: weightClass,
          red_athlete_record_id:  redArId,
          blue_athlete_record_id: blueArId,
          red_source_bout_id:  red.mode === 'bout' ? (redSourceBout || null) : null,
          blue_source_bout_id: blue.mode === 'bout' ? (blueSourceBout || null) : null,
        } satisfies BoutWriteBody;
        if (isEdit) {
          // Status guard is on the backend; frontend already hides it for scored bouts
          const res = await updateBout(bout!.id, {
            ...shared,
            status,
            stage: stage || null,  // a cleared stage goes as null: bouts_stage_check refuses ''
            // Only a change: the backend locks the rounds of a live bout (409).
            ...(sentRounds !== boutRounds(bout!) ? { rounds: sentRounds } : {}),
          });
          warnings = res.data?.warnings || [];
          if (juryId) {
            await assignBoutJury(tournamentId, bout!.id, juryId);
          }
        } else {
          // The stage is a required select too. The guard narrows the type: a create requires a stage.
          if (!stage) return;
          const res = await createBout({
            ...shared,
            tournament_id: tournamentId,
            bout_number: parseInt(boutNumber),
            stage,
            rounds: sentRounds,
            jury_user_id: juryId || null,
          });
          warnings = res.data?.warnings || [];
        }
        onDone(warnings);
      } catch (e: unknown) {
        // #504 review — the raw `error` of the answer was English for a
        // Lithuanian user and recorded nothing (`JURY_IS_RING_REF`, the refusal
        // of the tournament day, answers here too).
        setErr(inlineError(e, 'Error saving bout'));
        const advice = formatWarnings(warnings);
        if (advice) showWarning(advice);
      }
    });
  }

  const title = isEdit ? t.boutForm.editTitle.replace('{n}', String(bout!.bout_number)) : t.boutForm.createTitle;
  const isScored = isEdit && bout!.status === 'scored';

  // #552 — the panel layout: Save stays at the bottom of the dialog.
  return (
    <ModalBackdrop onClose={onClose} maxWidth="md" layout="panel" ariaLabel={title}>
      <ModalHeader
        title={title}
      />

      <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
        <ModalBody className="space-y-4">

          {/* Bout number — create only */}
          {!isEdit && (
            <Field label={t.boutForm.boutNumber} required>
              <input type="number" min="1" className="input" value={boutNumber}
                onChange={e => setBoutNumber(e.target.value)} />
            </Field>
          )}

          {/* Status — edit only, locked if scored */}
          {isEdit && (
            <Field group label={t.common.status}>
              {isScored ? (
                <div className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg border border-gray-200 text-gray-600">
                  <LockIcon className="w-4 h-4 shrink-0" />
                  <span className="text-xs font-semibold">{t.boutForm.scoredLocked}</span>
                </div>
              ) : (
                <>
                  <div className="text-xs text-gray-400 mb-1.5">{t.boutForm.statusHint}</div>
                  <div className="grid grid-cols-2 gap-1.5">
                    {(['pending', 'current'] as const).map(s => (
                      <button key={s} type="button" onClick={() => setStatus(s)}
                        className={`py-1.5 rounded-lg text-xs font-semibold border-2 transition-all ${
                          status === s
                            ? s === 'current' ? 'border-green-600 bg-green-600 text-white' : 'border-amber-500 bg-amber-500 text-white'
                            : 'border-gray-200 text-gray-500 hover:border-gray-400'}`}>
                        {s === 'current' ? t.boutForm.statusCurrent : t.boutForm.statusPending}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </Field>
          )}

          {/* Athletes — editable in both create and edit modes */}
          {genderMismatch && (
            <Notice tone="error" size="xs" icon={WarningIcon} className="font-medium">
              {t.boutForm.genderMismatch.replace('{red}', genderLabel(String(redGender))).replace('{blue}', genderLabel(String(blueGender)))}
            </Notice>
          )}
          {/* Red corner. It stays first in the DOM: officials-pickers-454 reads the first search field. */}
          <CornerPicker corner="red" state={red} options={redOptions} boutOptions={redBoutOptions} reg={redReg}
            gender={redGender} onAthlete={handleRedChange} onClassReset={resetClass} />

          {/* Blue corner */}
          <CornerPicker corner="blue" state={blue} options={blueOptions} boutOptions={blueBoutOptions} reg={blueReg}
            gender={blueGender} onAthlete={handleBlueChange} onClassReset={resetClass} />

          {/* Age group + Weight class */}
          <div className="grid grid-cols-2 gap-3">
            <Field label={t.bout.ageGroup} required hint={(redReg || blueReg) ? t.boutForm.auto : undefined}>
              <select className="input" value={ageGroup} data-testid="bout-form-age-group"
                onChange={e => { setAgeGroup(e.target.value); setWeightClass(''); }}>
                <option value="">— {t.common.select} —</option>
                {ageGroups.map((ag: AgeGroup) => (
                  <option key={ag.code} value={ag.code}>{ag.code}</option>
                ))}
              </select>
            </Field>
            <Field label={t.boutForm.weightClass} required>
              <select className="input" value={weightClass} data-testid="bout-form-weight-class" onChange={e => setWeightClass(e.target.value)}>
                <option value="">— {t.common.select} —</option>
                {wcOptions.length > 0
                  ? wcOptions.map((wc: WeightClass) => <option key={wc.id} value={wc.class_label}>{wc.class_label}</option>)
                  : weightClass
                    ? <option value={weightClass}>{weightClass}</option>
                    : null}
              </select>
            </Field>
          </div>
          {canFiveRounds && (
            <Field label={t.boutForm.rounds} help={roundsLocked ? t.boutForm.roundsLocked : undefined}>
              <select id="bout-form-rounds" className="input" data-testid="bout-form-rounds" value={rounds}
                disabled={roundsLocked} onChange={e => setRounds(Number(e.target.value))}>
                <option value={3}>{t.boutForm.rounds3}</option>
                <option value={5}>{t.boutForm.rounds5}</option>
              </select>
            </Field>
          )}
          {timeFormula && (
            <div className="text-xs text-gray-500 -mt-2 px-1">
              {t.boutForm.formula} <span className="font-semibold text-gray-700">{timeFormula}</span>{' '}
              {t.boutForm.rest.replace('{rest}', formatClock((ROUND_TIMING[ageGroup]?.restSec ?? 60) * 1000))}
            </div>
          )}

          {/* Ring */}
          <Field label={t.common.ring} required>
            <select className="input" value={ringId} onChange={e => setRingId(e.target.value)}>
              <option value="">— {t.common.select} —</option>
              {rings.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </Field>

          {/* Stage */}
          <Field label={t.common.stage} required>
            <select className="input" value={stage} onChange={e => setStage(e.target.value as Stage | '')}>
              <option value="">— {t.common.select} —</option>
              <option value="Challenge">Challenge</option>
              <option value="R32">R32</option>
              <option value="R16">R16</option>
              <option value="QF">QF</option>
              <option value="SF">SF</option>
              <option value="Final">Final</option>
            </select>
          </Field>

          {/* Tournament Day */}
          {tournamentDays.length > 0 && (
            <Field label={t.boutForm.day} optional={isMultiDay}>
              <select className="input" value={boutDate} onChange={e => setBoutDate(e.target.value)}>
                {isMultiDay && <option value="">{t.boutForm.notAssigned}</option>}
                {tournamentDays.map(day => (
                  <option key={day} value={day}>{formatDay(day, 'EEE dd MMM yyyy')}</option>
                ))}
              </select>
            </Field>
          )}

          {/* Ring Referee */}
          <Field label={t.bout.ringReferee} hint={t.boutForm.ringRefHint}>
            <select className="input" value={ringRefId} onChange={e => setRingRefId(e.target.value)} data-testid="bout-form-ring-ref">
              <option value="">{t.boutForm.none}</option>
              {ringRefOptions.map(r => <option key={r.id} value={r.id}>{personName(r.first_name, r.last_name)}</option>)}
            </select>
          </Field>

          {/* Jury */}
          <Field label={isEdit ? t.boutForm.assignJury : t.bout.jury}
            hint={isEdit ? t.boutForm.juryHintEdit : t.boutForm.juryHintCreate}>
            <select className="input" value={juryId} onChange={e => setJuryId(e.target.value)} data-testid="bout-form-jury">
              <option value="">{isEdit ? t.boutForm.keepExisting : t.boutForm.none}</option>
              {juryOptions.map(r => <option key={r.id} value={r.id}>{personName(r.first_name, r.last_name)}</option>)}
            </select>
          </Field>

          {/* Side Judges — edit only, informational */}
          {isEdit && sideJudgeIds.length > 0 && (
            <Field label={t.bout.sideJudges} group
              hint={t.boutForm.sideJudgesHint.replace('{n}', String(sideJudgeIds.length))}>
              <div className="flex flex-wrap gap-1 mt-1">
                {(bout!.referees || []).map((r: BoutReferee) => (
                  <span key={r.id} className="badge badge-gray text-xs">{personName(r.first_name, r.last_name)}</span>
                ))}
              </div>
            </Field>
          )}

          {err && <Notice tone="error" size="xs">{err}</Notice>}
        </ModalBody>

        <ModalFooter
          actions={isEdit && onDelete && !isScored ? [{
            label: t.common.delete, destructive: true, disabled: save.busy, testId: 'bout-delete',
            onClick: async () => {
              // A hard delete asks two questions (the rule of the operator):
              // "Delete bout #N?", then "Delete this bout permanently?". Each
              // caller of the form gets both. The second one starts with its
              // button off, so a double click cannot answer it.
              if (!(await confirm({ message: t.tournament.deleteBoutNumberConfirm.replace('{n}', String(bout!.bout_number)), confirmLabel: t.common.delete, destructive: true }))) return;
              if (!(await confirm({ message: t.tournament.deleteBoutConfirm, confirmLabel: t.common.delete, destructive: true }))) return;
              onDelete(bout!.id); onClose();
            },
          }] : undefined}
          saveAsSubmit
          busy={save.busy}
          saveLabel={isEdit ? t.boutForm.saveChanges : t.boutForm.createBout}
          saveDisabled={(!isEdit && !boutNumber) || genderMismatch}
          onCancel={onClose}
        />
      </form>
    </ModalBackdrop>
  );
}
